/**
 * Tax Deductions Page
 * Shows all tax-deductible items (expenses + subscriptions) grouped by IRS category
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Receipt, Package, TrendingDown } from 'lucide-react';

interface DeductionItem {
  sourceType: string;
  sourceId: string;
  description: string;
  amount: number;
  itemDate: string;
  taxCategory: string;
  vendor: string | null;
  categoryName: string | null;
  businessProfileName: string | null;
}

interface BusinessProfile {
  id: string;
  name: string;
}


const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

export function DeductionsPage() {
  const [taxYear, setTaxYear] = useState(currentYear);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [businessProfileId, setBusinessProfileId] = useState<string>('all');

  const { data: profilesData } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => api.getBusinessProfiles(),
  });
  const profiles = (profilesData?.data || []) as BusinessProfile[];

  const { data, isLoading } = useQuery({
    queryKey: ['tax-deductions', taxYear, selectedCategory, businessProfileId],
    queryFn: async () => {
      const result = await api.getTaxDeductions({
        taxYear,
        taxCategory: selectedCategory !== 'all' ? selectedCategory : undefined,
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return result.data as {
        expenses: DeductionItem[];
        subscriptions: DeductionItem[];
        all: DeductionItem[];
      };
    },
  });

  const allItems = data?.all || [];
  const totalAmount = allItems.reduce((sum, item) => sum + item.amount, 0);

  // Group by category for summary
  const categoryTotals = new Map<string, { count: number; total: number }>();
  for (const item of allItems) {
    const existing = categoryTotals.get(item.taxCategory) || { count: 0, total: 0 };
    existing.count++;
    existing.total += item.amount;
    categoryTotals.set(item.taxCategory, existing);
  }

  const sortedCategories = Array.from(categoryTotals.entries())
    .sort((a, b) => b[1].total - a[1].total);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tax Deductions</h1>
          <p className="text-muted-foreground">All tax-deductible expenses and subscriptions by IRS category</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={businessProfileId} onValueChange={setBusinessProfileId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Businesses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Businesses</SelectItem>
              {profiles.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={taxYear.toString()} onValueChange={(v) => setTaxYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(y => (
                <SelectItem key={y} value={y.toString()}>TY {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Deductions</CardTitle>
            <TrendingDown className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(totalAmount)}</div>
            <p className="text-xs text-muted-foreground">{allItems.length} deductible items</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">From Expenses</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency((data?.expenses || []).reduce((s, e) => s + e.amount, 0))}
            </div>
            <p className="text-xs text-muted-foreground">{(data?.expenses || []).length} expense items</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">From Subscriptions</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency((data?.subscriptions || []).reduce((s, e) => s + e.amount, 0))}
            </div>
            <p className="text-xs text-muted-foreground">{(data?.subscriptions || []).length} subscriptions (annualized)</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Category Filter Sidebar */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">IRS Categories</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <button
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                selectedCategory === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
              onClick={() => setSelectedCategory('all')}
            >
              All Categories ({allItems.length})
            </button>
            {sortedCategories.map(([cat, data]) => (
              <button
                key={cat}
                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex justify-between ${
                  selectedCategory === cat ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`}
                onClick={() => setSelectedCategory(cat)}
              >
                <span className="truncate mr-2">{cat}</span>
                <span className="shrink-0">{formatCurrency(data.total)}</span>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Deduction Items */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>
              {selectedCategory === 'all' ? 'All Deductible Items' : selectedCategory}
              <span className="text-muted-foreground font-normal ml-2">
                ({allItems.length} items &middot; {formatCurrency(totalAmount)})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : allItems.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No deductible items found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allItems.map((item) => (
                  <div
                    key={`${item.sourceType}-${item.sourceId}`}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {item.sourceType === 'expense' ? (
                        <Receipt className="h-4 w-4 text-muted-foreground shrink-0" />
                      ) : (
                        <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{item.description}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          {item.vendor && <span>{item.vendor}</span>}
                          <span>{formatDate(item.itemDate)}</span>
                          {item.businessProfileName && (
                            <span>&middot; {item.businessProfileName}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant="secondary">{item.taxCategory}</Badge>
                      <Badge variant={item.sourceType === 'expense' ? 'default' : 'info'}>
                        {item.sourceType}
                      </Badge>
                      <span className="font-medium text-emerald-600 w-24 text-right">
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
